import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useQuery, useQueryClient, useMutation } from 'react-query';
import 'react-loader-spinner/dist/loader/css/react-spinner-loader.css';
import { toast } from 'react-toastify';
import ScrollToTop from 'react-scroll-to-top';
import { PropertyCard } from './PropertyCard';
import { ReasonModal } from './ReasonModal';
import { LeaveFeedbackModal } from './modals/LeaveFeedbackModal';
import * as AppConstants from '../AppConstants';
import { useNavigate } from 'react-router-dom';
import { BuyerPropertyAppointmentModal } from './modals/BuyerPropertyAppointmentModal';
import moment from 'moment-timezone';
import { ScrollContext } from './ScrollContext';
import CenteredSpinner from './CenteredSpinner';


export const BuyerProperties = () => {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const location = useLocation();
  const [showModal, setShowModal] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const [reason, setReason] = useState('');
  const [checked, setChecked] = useState('');
  const [propertiesChecked, setPropertiesChecked] = useState('');
  const [description, setDescription] = useState('');
  const [comment, setComment] = useState('');
  const [propertyID, setPropertyID] = useState('');
  const [propertyRef, setPropertyRef] = useState(null);
  const [buyerPropertyId, setBuyerPropertyId] = useState('');
  const [disabled, setDisabled] = useState(false);
  const [reasonError, setReasonError] = useState('');
  const [commentError, setCommentError] = useState('');
  const [userID] = useState(localStorage.getItem('userId'));
  const [staffMemberId] = useState(localStorage.getItem('staffMemberId'));
  const [feedbackError, setFeedbackError] = useState('');
  const [appointmentError, setAppointmentError] = useState('');
  const [feedback, setFeedback] = useState('');
  const [startDate, setStartDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [buyerProperties, setBuyerProperties] = useState(location?.state?.properties);
  const [propertyTimes, setPropertyTimes] = useState({});
  const [selectedProperties, setSelectedProperties] = useState([]);
  const [filterNA, setFilterNA] = useState(false);
  const [loadingFilter, setLoadingFilter] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pageSize, setPageSize] = useState(12);
  const [totalRecords, setTotalRecords] = useState(null);
  const drawRef = React.useRef(1);

  const navigate = useNavigate();
  const { isBottom } = React.useContext(ScrollContext);


  useEffect(() => {
    document.title = 'Buyer Properties';
    window.scrollTo(0, 0);
  }, [location?.state?.properties]);

  const fetchBuyerPropertiesByFilter = async (filterNAValue, resetSelections = false) => {
    if (!location?.state?.buyerSpecId) return;
    setLoading(true);
    const buyerSpecId = parseInt(location?.state?.buyerSpecId, 10);
    const start = 0;
    const length = pageSize;
    const draw = drawRef.current;

    const columns = [
      { data: 'buyerPropertyId', name: 'buyerPropertyId', searchable: false, orderable: false },
      { data: '', name: 'actions', searchable: false, orderable: false },
      { data: 'propertyRef', name: 'propertyRef', searchable: true, orderable: true },
      { data: 'status', name: 'status', searchable: true, orderable: true },
      { data: 'comments', name: 'comments', searchable: true, orderable: true },
      { data: 'rating', name: 'rating', searchable: true, orderable: true },
      { data: 'searchDate', name: 'searchDate', searchable: true, orderable: true },
      { data: 'shortDescription', name: 'shortDescription', searchable: true, orderable: true },
      { data: '', name: 'propertyAddress', searchable: true, orderable: true },
      { data: '', name: 'price', searchable: true, orderable: true },
      { data: 'propertyMainOwnerName', name: 'propertyMainOwnerName', searchable: true, orderable: true },
      { data: 'propertyViewingsBy', name: 'propertyViewingsBy', searchable: true, orderable: true },
      { data: '', name: 'buyerAppointment', searchable: true, orderable: true },
      { data: '', name: 'ownerAppointment', searchable: true, orderable: true },
      { data: 'keyBookings', name: 'keyBookings', searchable: true, orderable: true },
      { data: 'keysAvailable', name: 'keysAvailable', searchable: true, orderable: true },
      { data: 'bedrooms', name: 'bedrooms', searchable: true, orderable: true },
      { data: 'localityName', name: 'localityName', searchable: true, orderable: true },
      { data: 'propertyTypeName', name: 'propertyTypeName', searchable: true, orderable: true },
      { data: 'propertySubTypeName', name: 'propertySubTypeName', searchable: true, orderable: true },
      { data: 'propertyAreaTypeName', name: 'propertyAreaTypeName', searchable: true, orderable: true },
      { data: 'propertyStateName', name: 'propertyStateName', searchable: true, orderable: true },
      { data: 'zoneName', name: 'zoneName', searchable: true, orderable: true },
      { data: 'propertyTypeGroupName', name: 'propertyTypeGroupName', searchable: false, orderable: true },
      { data: '', name: 'nextAvail', searchable: true, orderable: true },
      { data: 'areaSqMtrs', name: 'areaSqMtrs', searchable: true, orderable: true },
      { data: 'dateRegistered', name: 'dateRegistered', searchable: true, orderable: true },
      { data: 'ownerLastContacted', name: 'ownerLastContacted', searchable: true, orderable: true },
    ];

    const datatableColumns = columns.map((col) => ({
      data: col.data,
      name: col.name,
      searchable: Boolean(col.searchable),
      orderable: Boolean(col.orderable),
      search: { value: '', regex: false },
    }));

    const payload = {
      buyerSpecId: buyerSpecId,
      datatableRequest: {
        draw: draw,
        columns: datatableColumns,
        order: [{ column: 2, dir: 'asc' }],
        start: start,
        length: length,
        search: { value: '', regex: false },
      },
      filterNA: Boolean(filterNAValue),
    };

    const options = {
      method: 'POST',
      url: '/buyerproperty/GetBuyerPropertiesWithPaging',
      headers: {
        'Content-Type': 'application/json',
      },
      data: JSON.stringify(payload),
    };

    try {
      const response = await axios.request(options);
      // Support DataTables-like response or plain array
      const data = Array.isArray(response?.data?.data)
        ? response.data.data
        : Array.isArray(response?.data)
        ? response.data
        : [];
      setBuyerProperties(data);
      if (response?.data?.totalRecordsFiltered != null) {
        setTotalRecords(response.data.totalRecordsFiltered);
      } else if (response?.data?.totalRecords != null) {
        setTotalRecords(response.data.totalRecords);
      } else {
        setTotalRecords(data.length ?? 0);
      }
      if (resetSelections) {
        setChecked('');
        setPropertiesChecked('');
        setSelectedProperties([]);
        setPropertyTimes({});
      }
    } catch (error) {
      // Only notify if we don't have data to display
      if (!buyerProperties || buyerProperties.length === 0) {
        toast.error(`Sorry, server error, try again later`, {
          position: 'top-left',
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          draggable: true,
          progress: undefined,
        });
      }
    } finally {
      setLoading(false);
      setLoadingFilter(false);
      drawRef.current = drawRef.current + 1;
    }
  };

  // If you want to always use the new endpoint, uncomment below to load on mount
  // useEffect(() => {
  //   fetchBuyerPropertiesByFilter(filterNA);
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, []);

  const handleToggleFilterNA = async (e) => {
    const next = e.target.checked;
    drawRef.current = 1;
    setLoadingFilter(true);
    setFilterNA(next);
    // Reset selections; effect below will refetch using the new filter
    setChecked('');
    setPropertiesChecked('');
    setSelectedProperties([]);
    setPropertyTimes({});
  };

  // Infinite scroll increase of page size
  useEffect(() => {
    if (isBottom) {
      setPageSize((prev) => prev + 12);
    }
  }, [isBottom]);

  // Fetch on mount and when pageSize/filter/buyerSpecId changes
  useEffect(() => {
    fetchBuyerPropertiesByFilter(filterNA, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageSize, filterNA, location?.state?.buyerSpecId]);

  // Note: avoid early returns before hooks; show spinner within JSX instead

  const handleDelete = async (id) => {
    const options = {
      method: 'DELETE',
      url: `/BuyerProperty/${id.id}`,
      headers: {
        'Content-Type': 'application/json',
      },
    };
    axios
      .request(options)
      .then((response) => {
        toast.success(
          `Property: ${id.propertyRef} has been removed`,
          {
            position: 'top-center',
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            draggable: true,
            progress: undefined,
          },
        );
        removeBuyerProperty(id.id);
      })
      .catch((error) => {
        throw new Error('Server error');
      });
  };
  
  const removeBuyerProperty = (buyerPropertyId) => {
    let filteredArray = buyerProperties.filter(item => item.buyerPropertyId !== buyerPropertyId)
    setBuyerProperties(filteredArray);
  }

  const handleCheck = (event) => {
    const propertyId = event.target.value;
    const property = JSON.parse(event.target.getAttribute('data-property'));
  
    // Update checked list and propertiesChecked list
    var updatedList = [...checked];
    var propertyUpdatedList = [...propertiesChecked];
  
    if (event.target.checked) {
      updatedList = [...checked, propertyId];
      propertyUpdatedList = [...propertiesChecked, property];
    } else {
      updatedList.splice(checked.indexOf(propertyId), 1);
      propertyUpdatedList.splice(propertiesChecked.findIndex(p => p.id === property.id), 1);
    }
    setChecked(updatedList);
    setPropertiesChecked(propertyUpdatedList);
    
    // Update selectedProperties and propertyTimes
    setSelectedProperties(prevSelected => {
      let updatedSelected;
      if (event.target.checked) {
        updatedSelected = [...prevSelected, propertyId];
      } else {
        updatedSelected = prevSelected.filter(id => id !== propertyId);
      }
      return updatedSelected;
    });
  
    setPropertyTimes(prevTimes => {
      let updatedTimes = { ...prevTimes };
      if (event.target.checked) {
        updatedTimes[propertyId] = {
          startTime: null,
          endTime: null
        };
      } else {
        delete updatedTimes[propertyId];
      }
      return updatedTimes;
    });
  };

  const handleReasonChange = (event) => {
    setReasonError(null);
    setReason(event.value);
    setDescription(event.label);
  };

  const fetchReason = () => {
    const options = {
      method: 'GET',
      url: `/propertyfavouritereason`,
    };

    return axios
      .request(options)
      .then((response) => {
        let options = response.data.map((element) => {
          return {
            value: element.id,
            label: element.description,
          };
        });
        return options;
      })
      .catch((error) => {});
  };

  const mutation = useMutation(handleDelete, {
    onError: (err, _id, context) => {
      queryClient.setQueryData('Favourites', context.previousValue);
      toast.error(`Server error, Please try again later`, {
        position: 'top-left',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    },
  });

  const { data: reasonData } = useQuery(['Reason'], fetchReason, { refetchOnWindowFocus: false });

  const reasonModal = (e, id, propertyRef, localReason = null) => {
    if (localReason) {
      setReason(localReason);
    }
    e.preventDefault();
    e.stopPropagation();
    setShowModal(true);
    setPropertyID(id);
    setPropertyRef(propertyRef);
  };

  const feedbackModal = (e, propertyId, propertyRef, buyerPropertyId) => {
    e.preventDefault();
    e.stopPropagation();
    setShowFeedbackModal(true);
    setPropertyID(propertyId);
    setPropertyRef(propertyRef);
    setBuyerPropertyId(buyerPropertyId)
  };

  const appointmentModal = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (checked.length < 1) {
      //No properties selected, show error
      toast.error(
        `Select at least one property`,
        {
          position: 'top-center',
          autoClose: 2000,
          hideProgressBar: false,
          closeOnClick: true,
          draggable: true,
          progress: undefined,
        },
      );
      return;
    }
    setShowAppointmentModal(true);
  };

  const navigateViewingSheet = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (checked.length < 1) {
      //No properties selected, show error
      toast.error(
        `Select at least one property`,
        {
          position: 'top-center',
          autoClose: 2000,
          hideProgressBar: false,
          closeOnClick: true,
          draggable: true,
          progress: undefined,
        },
      );
      return;
    }
    navigate("/ViewingSheetForm", 
      { state: 
        { 
          properties: propertiesChecked,
          buyerName: location?.state?.buyerName,
          buyerSpecId: location?.state?.buyerSpecId,
          selectedPropertiesIds: checked,
          buyerEmail: location?.state?.buyerEmail
        }
      });
  };

  const handleComment = (e) => {
    setCommentError(null);
    setComment(e.target.value);
  };

  const handleFeedback = (e) => {
    setFeedbackError(null);
    setFeedback(e.target.value);
  };

  const handleTimeChange = (propertyId, type, value) => {
    setPropertyTimes((prevTimes) => ({
      ...prevTimes,
      [propertyId]: {
        ...prevTimes[propertyId],
        [type]: value,
      },
    }));
  };

  const handlePropertySelection = (propertyId) => {
    setSelectedProperties(prevSelected => {
      if (prevSelected.includes(propertyId)) {
        return prevSelected.filter(id => id !== propertyId);
      } else {
        return [...prevSelected, propertyId];
      }
    });
  };

  const addFavourite = async (e) => {
    setDisabled(true);
    if (!reason) {
      setReasonError("Reason input can't be emptied");
      setDisabled(false);
      return;
    }
    if (!comment) {
      setCommentError("Comment input can't be emptied");
      setDisabled(false);
      return;
    }

    e.preventDefault();
    e.stopPropagation();

    let d = new Date();
    let isoDate = d.toISOString();

    const config = {
      method: 'POST',
      url: '/propertyfavourite',
      headers: {
        'Content-Type': 'application/json',
      },
      data: JSON.stringify({
        propertyId: propertyID,
        sys_UserId: userID,
        dateRegistered: isoDate,
        propertyFavouriteReasonId: reason,
        comments: comment,
      }),
    };

    try {
      const data = await axios(config);
      if (data.status == 201) {
        setReason('');
        setComment('');
        setDisabled(false);
        toast.success(
          `Property: ${propertyRef} has been added to Favourites`,
          {
            position: 'top-center',
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            draggable: true,
            progress: undefined,
          },
        );
        setShowModal(false);
      }
    } catch (err) {
      if (
        err.response.status === 400 &&
        err.response.data.message ===
          'bizrule_propertyFavourite_favouriteReasonAlreadyExists'
      )
        toast.error(
          `Property: ${propertyRef} wasn't added, It has been already added`,
          {
            position: 'top-left',
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            draggable: true,
            progress: undefined,
          },
        );
      if (err.response.status === 500)
        toast.error(
          `Server error. Property: ${propertyRef} wasn't added, try again later`,
          {
            position: 'top-left',
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            draggable: true,
            progress: undefined,
          },
        );
      setComment('');
      setDisabled(false);
    }
  };

  const postPropertyFeedback = async (e) => {
    setDisabled(true);
    if (!feedback) {
      setFeedbackError("Feedback is required");
      setDisabled(false);
      return;
    }

    e.preventDefault();
    e.stopPropagation();

    let d = new Date();
    let isoDate = d.toISOString();

    const config = {
      method: 'POST',
      url: '/note/LeavePropertyFeedback',
      headers: {
        'Content-Type': 'application/json',
      },
      data: JSON.stringify({
        documentContextId: AppConstants.DocumentContextId_Property,
        noteContextId: AppConstants.NoteContextId_PropertyFeedback,
        recordId: propertyID,
        buyerPropertyId: buyerPropertyId,
        subject: 'Feedback from Buyer Ref ' + location?.state?.buyerRef,
        postedBy: localStorage.getItem('username'),
        noteDate: isoDate,
        staffMemberName: JSON.parse(localStorage.getItem('staffMemberInfo')).reportingName,
        noteText: feedback
      }),
    };

    try {
      const data = await axios(config);
      if (data.status == 200 || data.status == 201) {
        setFeedback('');
        setDisabled(false);
        toast.success(
          `Feedback posted on Property Ref ${propertyRef}`,
          {
            position: 'top-center',
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            draggable: true,
            progress: undefined,
          },
        );
        setShowFeedbackModal(false);
      }
    } catch (err) {
      if (
        err.response.status === 400 &&
        err.response.data.message ===
          'bizrule_propertyFavourite_favouriteReasonAlreadyExists'
      )
        toast.error(
          `Property: ${propertyRef} wasn't added, It has been already added`,
          {
            position: 'top-left',
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            draggable: true,
            progress: undefined,
          },
        );
      if (err.response.status === 500)
        toast.error(
          `Server error. Property: ${propertyRef} wasn't added, try again later`,
          {
            position: 'top-left',
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            draggable: true,
            progress: undefined,
          },
        );
      setComment('');
      setDisabled(false);
    }
  };

  const postAppointment = async (e) => {
    setDisabled(true);
    // if (!startDate || !startTime || !endTime) {
    //   setAppointmentError("Date and time is required");
    //   setDisabled(false);
    //   return;
    // }

    console.log('Selected Properties:', selectedProperties);
    console.log('Property checkedd:', propertiesChecked);

    e.preventDefault();
    e.stopPropagation();

    let d = new Date();
    let isoDate = d.toISOString();

    let data = [];

    checked.forEach((propertyId) => {
      const times = propertyTimes[propertyId] || {};
      data.push({
        AppointmentType: 1, // Appointment with Buyer
        RecordRefId: location?.state?.buyerSpecId,
        BuyerSpecId: location?.state?.buyerSpecId,
        PropertyId: parseInt(propertyId),
        AppointmentDate: moment(startDate).utc(true),
        AppointmentStart: moment(times.startTime).utc(true),
        AppointmentEnd: moment(times.endTime).utc(true),
        StaffMemberId: parseInt(staffMemberId),
        Status: 1, // Confirmed
      });
    });

    const config = {
      method: 'POST',
      url: '/Appointment/CreateBuyerAppointmentFromMobile',
      headers: {
        'Content-Type': 'application/json',
      },
      data: JSON.stringify(data),
    };
    
    try {
      const data = await axios(config);
      if (data.status == 200 || data.status == 201) {
        setFeedback('');
        setDisabled(false);
        toast.success(
          `Appointment created successfully`,
          {
            position: 'top-center',
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            draggable: true,
            progress: undefined,
          },
        );
        setShowAppointmentModal(false);
      }
    } catch (err) {
      if (err.response.status === 500)
      setComment('');
      setDisabled(false);
    }
  };


  return (
    <div className="container vh-100">
      {loading && (!buyerProperties || buyerProperties.length === 0) ? (
        <CenteredSpinner />
      ) : null}
      <ScrollToTop smooth color="#6f00ff" />
      <div className='row'>
        <div className="col-9 col-xl-9 col-xl-9 ml-0 p-0">
          <div className="h4 mb-3 ml-0 font-weight-bold">
            <i className="fas fa-home" aria-hidden="true"></i>
            {location?.state?.buyerName} Properties
          </div>
        </div>
        <div className="col-3 col-md-3 col-xl-3 text-right">
          <p className="btn btn-outline-secondary d-block" onClick={() => navigate(-1)}>
            <i className="fas fa-arrow-left me-2" style={{ color: '#6c757d' }}></i>
            Back
          </p>
        </div>
      </div>
      <div className="d-flex align-items-center justify-content-between mb-3 ml-0">
        <div className="h6 mb-0 ml-0">
          You have {totalRecords ?? 0} listed {totalRecords === 1 ? 'property' : 'properties'}.
        </div>
        <div className="d-flex align-items-center">
          <input
            id="filterNA-toggle"
            type="checkbox"
            className="mr-2"
            checked={filterNA}
            onChange={handleToggleFilterNA}
            disabled={loadingFilter}
          />
          <label htmlFor="filterNA-toggle" className="mb-0">
            {loadingFilter ? 'Filtering...' : 'Filter N/A'}
          </label>
        </div>
      </div>
      <div className="row">
        <div className="col-12">
          <div className="form-group has-search w-100">
            <span className="fa fa-search form-control-feedback"></span>
            <input
              type="text"
              className="form-control"
              placeholder="Search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
      </div>
        
      <div className="row">
        <div className="col-6 text-center">
          <button className="btn btn-primary mb-4" onClick={appointmentModal} >
            Appointment
          </button>
        </div>
        <div className="col-6 text-center">
          <button className="btn btn-primary mb-4" onClick={navigateViewingSheet} >
            Viewing Sheet
          </button>
        </div>
      </div>

      <div className="row">
        {buyerProperties?.length < 1 ? (
          <h4 className="mx-auto">No search results found</h4>
        ) : null}
        <div className="container pl-0 pr-0">
          <div className="row mb-4">
            {buyerProperties &&
              React.Children.toArray(
                buyerProperties?.map((row, index) => (
                  <PropertyCard
                    key={row.buyerPropertyId || row.propertyId || row.propertyRef}
                    property={{
                      id: row.propertyId,
                      propertyRef: row.propertyRef,
                      forSaleOrToLet: row.forSaleOrToLet,
                      coverImg: ((row.externalFiles && row.externalFiles.length > 0 && row.externalFiles[0]?.filePath) ? row.externalFiles[0].filePath : row.coverImg),
                      showingOnWeb: row.showingOnWeb,
                      propertyTypeName: row.propertyTypeName,
                      propertySubTypeName: row.propertySubTypeName,
                      localityName: row.localityName,
                      areaSqMtrs: row.areaSqMtrs,
                      propertyStateName: row.propertyStateName,
                      bedrooms: row.bedrooms,
                      propertyToLet: row.forSaleOrToLet === true ? { longLetRate: row.longLetXRate, leaseRatePeriod: row.leaseRatePeriod, lettingNextAvail: row.nextAvailable } : undefined,
                      propertyForSale: row.forSaleOrToLet === false ? { price: isNaN(parseFloat(row.price)) ? 0 : parseFloat(row.price) } : undefined,
                    }}
                    index={index}
                    location={location}
                    mutation={mutation}
                    showHotBtn={true}
                    showFeedbackBtn={true}
                    showSelectCheckbox={true}
                    reasonModal={reasonModal}
                    feedbackModal={feedbackModal}
                    buyerProperty={{ id: row.buyerPropertyId, rating: row.rating }}
                    handleCheck={handleCheck}
                    showRemoveBuyerPropertyBtn={true}
                   />
                )),
              )}
          </div>
          <p className="btn btn-outline-secondary d-block mb-4" onClick={() => navigate(-1)}>
            <i className="fas fa-arrow-left me-2" style={{ color: '#6c757d' }}></i>
            Back
          </p>
        </div>
      </div>
      <ReasonModal
          showModal={showModal}
          reason={reason}
          setReason={setReason}
          setReasonError={setReasonError}
          setCommentError={setCommentError}
          setShowModal={setShowModal}
          reasonError={reasonError}
          handleReasonChange={handleReasonChange}
          reasonData={reasonData}
          comment={comment}
          handleComment={handleComment}
          commentError={commentError}
          disabled={disabled}
          addFavourite={addFavourite}
        />
        <LeaveFeedbackModal
          showModal={showFeedbackModal}
          setFeedbackError={setFeedbackError}
          setShowModal={setShowFeedbackModal}
          feedback={feedback}
          handleFeedback={handleFeedback}
          feedbackError={feedbackError}
          disabled={disabled}
          postFeedback={postPropertyFeedback}
        />
        <BuyerPropertyAppointmentModal
          showModal={showAppointmentModal}
          setAppointmentError={setAppointmentError}
          setShowModal={setShowAppointmentModal}
          startDate={startDate}
          setStartDate={setStartDate}
          propertyTimes={propertyTimes}
          handleTimeChange={handleTimeChange}
          selectedProperties={selectedProperties}
          propertiesChecked={propertiesChecked}
          appointmentError={appointmentError}
          disabled={disabled}
          postAppointment={postAppointment}
        />
    </div>
  );
};
